import { useEffect, useRef } from 'react';

// SAB-657: embedded Soap checkout. Renders the hosted checkout in an
// <iframe allow="payment; geolocation"> (geolocation added by SAB-1092)
// so a card deposit never leaves the app (native WKWebView) or the page (web) — replacing the
// old `window.location.href = url` full-page redirect that stranded users outside the app.
//
// Completion handshake (Soap Embedded Checkout): on success Soap redirects INSIDE this frame to
// our same-origin return_url (…?soap_return=1). That page is served by our own app; main.tsx
// detects the framed return and posts { type: 'soap:checkout-complete' } to the parent, which we
// catch here. The webhook remains the source of truth for money — this signal only advances the
// UI to the pending-row poll.
//
// Requires (see vercel.json): Permissions-Policy `payment=(self <soap-origins>)` or the iframe's
// allow="payment" (and Apple Pay) is silently blocked; and X-Frame-Options SAMEORIGIN so the
// same-origin return page can render in-frame to post the signal.
//
// SAB-1092: the SAME pairing is required for GEOLOCATION, and it was missing. Soap's hosted
// checkout requests a position inside this frame; the browser refused because the parent had
// `geolocation=(self)` -- the feature was named but the Soap origins were NOT delegated -- and
// the iframe did not ask for it either. Soap then rendered "Location Access Required", which is
// SOAP's string, not ours (it appears nowhere in this repo). Both halves are needed: the header
// delegates the capability to those origins, the allow attribute asks for it. Either alone
// fails SILENTLY, which is what made this look like a broken geolocation stack rather than a
// missing delegation -- the same trap as the Persona camera policy.
//
// This is NOT the Soap device-ping, which is fire-and-forget telemetry and blocks nothing; its
// `no_client_position` outcome is by design (it never prompts) and was a red herring here.

// Soap checkout origins allowed to be the source of a completion message. Same list is mirrored
// in vercel.json's payment allowlist. Sandbox now; production origin TBD (confirm with Soap at
// cutover — see SAB-657).
const SOAP_CHECKOUT_ORIGINS = [
  'https://wallet-sandbox.paywithsoap.com',
  'https://wallet.paywithsoap.com',
];

interface SoapCheckoutFrameProps {
  /** The hosted checkout URL returned by soap-create-checkout. */
  url: string;
  /** Fired once when Soap signals completion. Start the pending-row poll here. */
  onComplete: () => void;
  /** Fired when the user dismisses the sheet without completing. */
  onCancel: () => void;
}

export function SoapCheckoutFrame({ url, onComplete, onCancel }: SoapCheckoutFrameProps) {
  const done = useRef(false);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Trust only our own return page (same origin) or a known Soap checkout origin.
      const trusted =
        e.origin === window.location.origin || SOAP_CHECKOUT_ORIGINS.includes(e.origin);
      if (!trusted) return;
      if ((e.data as { type?: string } | null)?.type === 'soap:checkout-complete' && !done.current) {
        done.current = true;
        onComplete();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onComplete]);

  // SAB-657 native: fixed inset-0 escapes #root's safe-area padding, so the overlay handles the
  // home indicator itself (paddingBottom) — else Soap's pay button sits under it.
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-background"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Our close control is a labelled "Cancel" on the LEFT — deliberately NOT an X, and NOT
          top-right where Soap renders its own X inside the iframe (two right-aligned controls
          doing different things is a mis-tap risk). This is the reliable escape (clean close, no
          poll); Soap's in-widget X gives no distinguishable cancel signal (their docs emit only
          soap:checkout-complete), so that path falls through to the bounded, honest poll.
          paddingTop clears the iOS status bar (matches the TopBar.tsx idiom). */}
      <div
        className="relative flex flex-shrink-0 items-center border-b border-border px-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
      >
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-sm font-medium">
          Complete your deposit
        </span>
      </div>
      {/* iOS WKWebView sizes an <iframe> to its CONTENT, ignoring flex:1 for height — Soap's
          100vh page then centers ~500px down. Pin the iframe to a definite box: a flex-1
          relative wrapper + an absolutely-filled iframe, so Soap's viewport is the overlay's
          remaining height, not the full screen. */}
      <div className="relative min-h-0 flex-1">
        <iframe
          title="Soap checkout"
          src={url}
          allow="payment; geolocation"
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    </div>
  );
}

export default SoapCheckoutFrame;
